<template>
  <section class="mt-4">
    <div :class="{ 'px-5 hmt-text': $vuetify.display.mdAndDown }">
      <h1>
        {{
          $t(
            "horses:horseProfilePage.carebook.list.heading",
            "Horse´s veterinary treatment, medications and suppliments "
          )
        }}
      </h1>
      <p
        v-html="
          $t(
            'horses:horseProfilePage.carebook.list.hint',
            'Includes records of veterinary treatments, medications and suppliments pursuant to'
          )
        "
      ></p>
    </div>

    <div>
      <AppTableHeader single-col>
        <template #left>
          <div class="d-flex flex-column flex-md-row justify-md-end">
            <div>
              <v-btn
                v-if="
                  $allowedMutation(
                    horse.allowedMutations,
                    'horseMedicalTreatmentCreate'
                  )
                "
                class="btn-with-icon px-2 text-white"
                variant="text"
                data-test="newMedicalTreatment"
                :to="{
                  name: 'HorseMedicalTreatmentFormNew',
                  params: { horseId: horse.id }
                }"
              >
                <div class="bg-edit btn-icon-with-text">
                  <v-icon size="small" class="text-white">$iconPlus</v-icon>
                </div>
                {{
                  $t(
                    "horses:horseCarebook.horseMedicalTreatment.addRecord.button",
                    "Add medical treatment record"
                  )
                }}
              </v-btn>
            </div>
            <div>
              <AppPrint
                element-id="#horseMedicalTreatments"
                color="white"
                button-class="px-2"
              >
                <template #button-content>
                  <div class="btn-icon-with-text">
                    <v-icon size="small" class="text-white">$iconPrint</v-icon>
                  </div>
                  {{ $t("common:button.exportPrint", "Print") }}
                </template>
              </AppPrint>
            </div>
            <div>
              <v-btn
                v-if="canViewDiscards"
                variant="text"
                class="btn-with-icon text-white px-2"
                @click="() => (includeDiscarded = !includeDiscarded)"
              >
                <div class="btn-icon-with-text">
                  <v-icon size="small" class="text-white">{{
                    includeDiscarded ? "$iconEyeSlash" : "$iconEye"
                  }}</v-icon>
                </div>
                {{
                  includeDiscarded
                    ? $t(
                        "horses:horseCarebook.horseMedicalTreatment.hideDiscardedRecords.button",
                        "Hide discarded records"
                      )
                    : $t(
                        "horses:horseCarebook.horseMedicalTreatment.showDiscardedRecords.button",
                        "Show discarded records"
                      )
                }}
              </v-btn>
            </div>
          </div>
        </template>
      </AppTableHeader>

      <div id="horseMedicalTreatments">
        <AppPrintBasicHorseDetails :horse="horse" />

        <AppTable
          :loading="loading"
          :items="horseMedicalTreatments"
          table-class="horse-medical-treatments-table"
        >
          <template #headers>
            <tr>
              <th>
                {{
                  $t(
                    "horses:horseCarebook.horseMedicalTreatment.startDateandTime.label",
                    "Date / Time"
                  )
                }}
              </th>
              <TableVerticalDivider
                tag="th"
                color="grey"
                class="hidden-md-and-up"
              />
              <th class="hidden-sm-and-down">
                {{
                  $t(
                    "horses:horseCarebook.horseMedicalTreatment.medicalTreatmentRecord.label",
                    "Medical Treatment Record"
                  )
                }}
              </th>
              <th class="hidden-sm-and-down">
                {{
                  $t(
                    "horses:horseCarebook.horseMedicalTreatment.filesDocuments.label",
                    "Files and documents"
                  )
                }}
              </th>
              <th class="hidden-sm-and-down">
                {{
                  $t(
                    "horses:horseCarebook.horseMedicalTreatment.note.label",
                    "Comments"
                  )
                }}
              </th>

              <th>
                {{
                  $t(
                    "horses:horseCarebook.horseMedicalTreatment.source.label",
                    "Source"
                  )
                }}
              </th>
              <TableVerticalDivider
                tag="th"
                color="grey"
                class="hidden-md-and-up"
              />

              <th class="d-print-none"></th>
            </tr>
          </template>
          <template #items="{ toggleItemExpand, itemIsExpanded }">
            <template v-for="item in horseMedicalTreatments" :key="item.id">
              <tr class="hmtt-main" :data-scroll-focus="`treatment-${item.id}`">
                <td style="">
                  <div>{{ $dayjs.date(item.startDate) }}</div>
                  <div>{{ item.startTime }}</div>
                  <div v-if="item.isDiscarded">
                    <AppChip color="red" text-color="text-white">{{
                      $t(
                        "horses:horseCarebook.horseMedicalTreatment.discardedRecord.badge",
                        "Discarded"
                      )
                    }}</AppChip>
                  </div>
                </td>

                <TableVerticalDivider
                  tag="td"
                  color="black"
                  class="hidden-md-and-up"
                />

                <td class="hidden-sm-and-down">
                  <HorseMedicalTreatmentDetails :item="item" />
                </td>

                <td class="hidden-sm-and-down">
                  <div v-for="doc in item.documents" :key="doc.id" class="mb-2">
                    <a :href="prependApiDomain(doc.path)" target="_blank">{{
                      truncate(doc.filename, { length: 75 })
                    }}</a>
                  </div>
                </td>

                <td class="hidden-sm-and-down">
                  {{ item.note }}
                </td>

                <td>
                  <HorseMedicalTreatmentSource :item="item" />
                </td>

                <TableVerticalDivider
                  tag="td"
                  color="black"
                  class="hidden-md-and-up"
                />

                <td class="text-end d-print-none">
                  <div>
                    <v-menu
                      v-if="
                        $allowedMutation(item.allowedMutations, [
                          'horseMedicalTreatmentUpdate',
                          'horseMedicalTreatmentDiscard'
                        ])
                      "
                      min-width="120px"
                      nudge-bottom="20"
                    >
                      <template #activator="{ props: attrs }">
                        <v-btn-icon
                          color="primary"
                          size="small"
                          icon
                          variant="outlined"
                          v-bind="attrs"
                        >
                          <v-icon size="small" color="primary"
                            >$iconEllipsisV</v-icon
                          >
                        </v-btn-icon>
                      </template>

                      <v-list>
                        <v-list-item
                          v-if="
                            $allowedMutation(
                              item.allowedMutations,
                              'horseMedicalTreatmentUpdate'
                            )
                          "
                          :to="{
                            name: 'HorseMedicalTreatmentFormEdit',
                            params: {
                              horseId: horse.id,
                              medicalTreatmentId: item.id
                            }
                          }"
                          class="text-black"
                        >
                          <v-icon size="small" start>$edit</v-icon>

                          {{
                            $t(
                              "horses:horseCarebook.horseMedicalTreatment.editRecord.button",
                              "edit record"
                            )
                          }}
                        </v-list-item>
                        <v-list-item
                          v-if="
                            item.changeLogChanges &&
                            item.changeLogChanges.length > 1
                          "
                          class="text-black"
                          @click="() => openChangeLogs(item)"
                        >
                          <v-icon size="small" start>$iconClock</v-icon>

                          {{
                            $t(
                              "horses:horseCarebook.horseMedicalTreatment.showRecordLog.button",
                              "open log"
                            )
                          }}
                        </v-list-item>
                        <v-list-item
                          v-if="
                            $allowedMutation(
                              item.allowedMutations,
                              'horseMedicalTreatmentDiscard'
                            )
                          "
                          class="text-black bg-alert"
                          @click="() => discardRecord(item)"
                        >
                          <v-icon size="small" start>$iconTrash</v-icon>

                          {{
                            $t(
                              "horses:horseCarebook.horseMedicalTreatment.discardRecord.button",
                              "discard record"
                            )
                          }}
                        </v-list-item>
                      </v-list>
                    </v-menu>

                    <v-btn-expand
                      v-if="$vuetify.display.smAndDown"
                      icon
                      size="small"
                      variant="outlined"
                      @click="() => toggleItemExpand(item.id)"
                    >
                      <v-icon size="small" color="primary">
                        {{
                          itemIsExpanded(item.id)
                            ? "$iconCaretDown"
                            : "$iconCaretRight"
                        }}
                      </v-icon>
                    </v-btn-expand>
                  </div>
                </td>
              </tr>

              <tr
                v-if="$vuetify.display.smAndDown"
                v-show="itemIsExpanded(item.id)"
                :key="item.id + 'expanded'"
                class="bg-alternate"
              >
                <td colspan="99" class="py-3">
                  <div v-if="item.veterinarian" class="mb-3 px-2">
                    <b>{{
                      $t(
                        "horses:horseCarebook.horseMedicalTreatment.veterinarian.label",
                        "Veterinarian"
                      )
                    }}</b>
                    <span style="text-decoration: underline" class="ml-2">{{
                      item.veterinarian
                    }}</span>
                  </div>

                  <div
                    v-if="item.diagnosis"
                    class="mb-3 hmtt-outlined-box hmtt-outlined-box-darker"
                  >
                    <b>{{
                      $t(
                        "horses:horseCarebook.horseMedicalTreatment.diagnosis.label",
                        "Diagnosis / Symptoms"
                      )
                    }}</b>
                    <div>
                      {{ item.diagnosis }}
                    </div>
                  </div>

                  <div
                    v-if="item.description"
                    class="mb-3 hmtt-outlined-box hmtt-outlined-box-darker"
                  >
                    <b>{{
                      $t(
                        "horses:horseCarebook.horseMedicalTreatment.description.label",
                        "Treatment description"
                      )
                    }}</b>
                    <div>
                      {{ item.description }}
                    </div>
                  </div>

                  <div
                    v-if="item.dosage || item.administration"
                    class="mb-3 hmtt-outlined-box hmtt-outlined-box-darker d-flex"
                  >
                    <div v-if="item.dosage">
                      <b>{{
                        $t(
                          "horses:horseCarebook.horseMedicalTreatment.dosage.label",
                          "Quantity / dosage"
                        )
                      }}</b>
                      <div>
                        {{ item.dosage }}
                      </div>
                    </div>
                    <div v-if="item.dosage && item.administration" class="px-4">
                      <div class="hmtt-vertical-divider"></div>
                    </div>
                    <div v-if="item.administration">
                      <b>{{
                        $t(
                          "horses:horseCarebook.horseMedicalTreatment.administration.label",
                          "Administration"
                        )
                      }}</b>
                      <div>
                        {{ item.administration }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="item.productComposition"
                    class="mb-3 hmtt-outlined-box"
                  >
                    <b>{{
                      $t(
                        "horses:horseCarebook.horseMedicalTreatment.productComposition.label",
                        "Product composition"
                      )
                    }}</b>
                    <div>
                      {{ item.productComposition }}
                    </div>
                  </div>

                  <div v-if="item.supplements" class="mb-3 hmtt-outlined-box">
                    <b>{{
                      $t(
                        "horses:horseCarebook.horseMedicalTreatment.supplements.label",
                        "Feed supplements"
                      )
                    }}</b>
                    <div>
                      {{ item.supplements }}
                    </div>
                  </div>

                  <div class="mb-3 hmtt-outlined-box">
                    <div>
                      <b>{{
                        $t(
                          "horses:horseCarebook.horseMedicalTreatment.note.label",
                          "Comments"
                        )
                      }}</b>
                    </div>
                    {{ item.note }}
                  </div>

                  <div v-if="item.documents.length" class="d-flex d-row">
                    <div class="mb-2 pr-4">
                      <b>{{
                        $t(
                          "horses:horseCarebook.horseMedicalTreatment.filesDocuments.label",
                          "Files and documents"
                        )
                      }}</b>
                    </div>
                    <div>
                      <div
                        v-for="doc in item.documents"
                        :key="doc.id"
                        class="mb-2"
                      >
                        <a :href="prependApiDomain(doc.path)" target="_blank">{{
                          truncate(doc.filename, { length: 75 })
                        }}</a>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </AppTable>
      </div>
    </div>

    <AppChangeLogsViewer
      ref="changeLogs"
      entity-typename="HorseMedicalTreatment"
    />
    <AppConfirm ref="discardConfirmation">
      <template #header>
        {{
          $t(
            "horses:horseCarebook.horseMedicalTreatment.discardRecord.modal.heading",
            "Discard horse medical treatment record"
          )
        }}
      </template>
      <template v-if="medicalTreatmentRemoval" #default>
        {{
          $t(
            "horses:horseCarebook.horseMedicalTreatment.discardRecord.modal.question",
            "You are going to discard the horse´s medical treatment record. Please note that this action cannot be reversed."
          )
        }}

        <AppShowHorseName :horse="horse" class="my-2" no-link />

        <div>{{ $dayjs.date(medicalTreatmentRemoval.startDate) }}</div>
        <div class="mb-2">{{ medicalTreatmentRemoval.startTime }}</div>

        <HorseMedicalTreatmentDetails :item="medicalTreatmentRemoval" />
      </template>
    </AppConfirm>
  </section>
</template>
<script>
export default { name: "HorseMedicalTreatments" };
</script>
<script setup>
import { shallowRef, ref, computed, nextTick } from "vue";
import gql from "fraql";
import { useQuery } from "@vue/apollo-composable";
import allowedMutation from "@/utils/allowedMutation";
import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppTableHeader from "@/components/elements/table/AppTableHeader.vue";
import AppPrintBasicHorseDetails from "@elements/prints/AppPrintBasicHorseDetails.vue";
import AppChip from "@elements/AppChip.vue";
import AppPrint from "@elements/AppPrint.vue";
import AppChangeLogsViewer from "@elements/changeLogs/AppChangeLogsViewer.vue";
import AppTable from "@elements/table/AppTable.vue";
import TableVerticalDivider from "@elements/table/TableVerticalDivider.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import HorseMedicalTreatmentDetails from "./HorseMedicalTreatmentDetails.vue";
import AppConfirm from "@/components/elements/AppConfirm.vue";
import HorseMedicalTreatmentSource from "./HorseMedicalTreatmentSource.vue";

import dayjs from "@/plugins/dayjs";
import { truncate, cloneDeep } from "lodash-es";
import { injectSnackbarError } from "@/composables/injections";
import { apolloClient } from "@/plugins/apollo";
import { useRouteScroll } from "@composables/route_scroll";
import { prependApiDomain } from "@/utils/prependApiDomain";

const props = defineProps({ horse: { type: Object, required: true } });

const { scrollIntoElement } = useRouteScroll();
const medicalTreatmentRemoval = ref();
const discardConfirmation = shallowRef();
const { snackbarServerError } = injectSnackbarError();
const changeLogs = ref();

const includeDiscarded = ref(false);
const horseMedicalTreatments = ref([]);

const canViewDiscards = computed(() =>
  allowedMutation(
    props.horse.allowedFields,
    "horseMedicalTreatmentsIncludingDiscarded"
  )
);

const { onResult, loading } = useQuery(
  gql`
    query horseMedicalTreatments($horseId: ID, $includeDiscarded: Boolean) {
      horse(id: $horseId) {
        id
        horseMedicalTreatments(includeDiscarded: $includeDiscarded) {
          id
          startDate
          startTime

          isDiscarded
          note

          diagnosis
          description
          administration
          supplements
          veterinarian
          productComposition
          dosage

          documents {
            id
            filename
            path
          }

          ${HorseMedicalTreatmentSource.fragments.horseMedicalTreatmentFields}
          changeLogChanges {
            version
          }
          allowedMutations
          createdAt
          updatedAt
          discardedAt
        }
      }
    }
  `,
  () => ({ horseId: props.horse.id, includeDiscarded: includeDiscarded.value })
);

onResult(({ loading, data }) => {
  if (loading) return;

  horseMedicalTreatments.value = cloneDeep(
    data.horse.horseMedicalTreatments
  ).sort((a, b) => {
    const diffs = dayjs(a.startDate).diff(b.startDate, "days");
    if (diffs === 0) {
      return dayjs(a.startTime, "HH:mm:ss").diff(
        dayjs(b.startTime, "HH:mm:ss")
      ) > 0
        ? -1
        : 1;
    }
    return diffs > 0 ? -1 : 1;
  });

  nextTick(() => {
    scrollIntoElement();
  });
});

async function discardRecord(medicalTreatment) {
  try {
    medicalTreatmentRemoval.value = medicalTreatment;
    const response = await discardConfirmation.value.blankSlateOpen();

    if (!response) return;

    await apolloClient.mutate({
      mutation: gql`
        mutation horseMedicalTreatmentDiscard($id: ID!) {
          horseMedicalTreatmentDiscard(input: { id: $id }) {
            horseMedicalTreatment {
              id
              isDiscarded
              allowedMutations
              discardedByPerson {
                id
                ${AppShowPersonName.fragments.person}
              }
              discardedAt
            }
          }
        }
      `,
      variables: { id: medicalTreatment.id }
    });
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  }
}

function openChangeLogs(medicalTreatment) {
  changeLogs.value.openChangeLogs(medicalTreatment.id);
}
</script>
<style lang="scss">
.hmt-text {
  @media (max-width: 959px) {
    h1 {
      font-size: 18px;
    }
  }
}

.horse-medical-treatments-table {
  @media print {
    width: 100%;
  }

  thead tr {
    th {
      text-align: start;
      font-size: 14px;
    }
  }
  tbody tr {
    td {
      vertical-align: top;
    }
  }

  @media (max-width: 959px) {
    font-size: 12px;

    thead tr {
      background: rgb(var(--v-theme-secondary));
      @media print {
        background: transparent;
      }
      th:first-child {
        text-align: center;
      }
      th {
        color: white !important;
        text-align: center;
        @media print {
          color: black !important;
        }
      }
    }

    tbody tr {
      &.hmtt-main {
        td:first-child {
          text-align: center;
        }
      }

      td {
        vertical-align: middle;
      }

      td:last-child {
        width: 105px;
      }
    }

    .hmtt-source-timestamp {
      color: rgb(var(--v-theme-muted));
    }

    .hmtt-vertical-divider {
      width: 2px;
      background: #535353;
      height: 100%;
    }
    .hmtt-outlined-box {
      border: 2px solid rgb(var(--v-theme-muted));

      border-radius: 5px;
      min-height: 51px;
      padding: 10px;

      &.hmtt-outlined-box-darker {
        border-color: rgb(var(--v-theme-muted-darken-2));
      }
    }
  }
}
</style>
